import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  discardDocIntelligenceUpload,
  listDocIntelligenceTemplates,
  reprocessDocIntelligenceUpload,
  uploadDocIntelligenceFile,
  uploadDocIntelligenceProjectFile
} from '../../services/api/implementation/impl';
import DocIntelApplyPanel from './DocIntelApplyPanel';
import DocIntelMappingPanel from './DocIntelMappingPanel';
import DocIntelReviewPanel from './DocIntelReviewPanel';
import DocIntelSidebar from './DocIntelSidebar';
import DocIntelTemplatePanel from './DocIntelTemplatePanel';
import DocIntelUploadPanel from './DocIntelUploadPanel';
import {
  btnPrimary,
  DocIntelMainCard,
  DocIntelPhaseRail,
  DocIntelStatePanel
} from './DocIntelUi';
import { getPanelForUpload, getPanelMeta, getReviewSessionId, getUploadId } from './docIntelUtils';
import { useDocIntelUploads } from './useDocIntelUploads';

function templateSessionStorageKey(projectId, dealId) {
  return `docIntel.templateBySession.${projectId ?? 'x'}.${dealId ?? 'x'}`;
}

function readStoredTemplateBySession(projectId, dealId) {
  try {
    const raw = sessionStorage.getItem(templateSessionStorageKey(projectId, dealId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const DocIntelWorkspace = ({
  dealId,
  projectId,
  mode = 'deal',
  onPromoted,
  openReviewSessionId = null,
  onOpenReviewSessionConsumed
}) => {
  const isTemplateMode = mode === 'template';
  const {
    uploads,
    selectedUpload,
    selectedUploadId,
    reviewSessionId,
    selectUpload,
    seedUpload,
    invalidateAll,
    isLoading,
    isFetching,
    isError,
    refetchList
  } = useDocIntelUploads({ dealId, projectId });

  const [applyResult, setApplyResult] = useState(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [templateStudioOpen, setTemplateStudioOpen] = useState(isTemplateMode);
  /** Force Review panel even after approve (revisit extracted field values). */
  const [forceReview, setForceReview] = useState(false);
  const [templateBySession, setTemplateBySession] = useState(() =>
    readStoredTemplateBySession(projectId, dealId)
  );

  // Open Review from Saved templates / Template editor.
  useEffect(() => {
    if (!openReviewSessionId) return;
    if (isLoading) return;
    const match = uploads.find(
      (u) => Number(getReviewSessionId(u)) === Number(openReviewSessionId)
    );
    if (match) {
      selectUpload(getUploadId(match));
      setForceReview(true);
      onOpenReviewSessionConsumed?.();
      return;
    }
    if (!isFetching) {
      toast.error('Could not find the source upload for this template’s review fields');
      onOpenReviewSessionConsumed?.();
    }
  }, [
    openReviewSessionId,
    uploads,
    isLoading,
    isFetching,
    selectUpload,
    onOpenReviewSessionConsumed
  ]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        templateSessionStorageKey(projectId, dealId),
        JSON.stringify(templateBySession)
      );
    } catch {
      /* ignore quota */
    }
  }, [templateBySession, projectId, dealId]);

  // Recover template ids from server drafts after refresh / new tab.
  useEffect(() => {
    if (!isTemplateMode || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { templates } = await listDocIntelligenceTemplates(projectId, 1, 100);
        if (cancelled || !Array.isArray(templates)) return;
        const recovered = {};
        for (const tpl of templates) {
          const sessionId = tpl.source_review_session_id;
          if (sessionId && !recovered[sessionId]) {
            recovered[sessionId] = tpl.id;
          }
        }
        if (Object.keys(recovered).length) {
          setTemplateBySession((prev) => ({ ...recovered, ...prev }));
        }
      } catch {
        /* list may fail without manage permission — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTemplateMode, projectId]);

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      setUploadingCount(files.length);
      const results = [];
      for (const file of files) {
        if (isTemplateMode) {
          results.push(await uploadDocIntelligenceProjectFile(projectId, file));
        } else {
          results.push(
            await uploadDocIntelligenceFile(dealId, file, { source_type: 'deal_attachment' })
          );
        }
      }
      return results;
    },
    onSuccess: (results) => {
      toast.success(
        results.length === 1 ? 'Upload started' : `${results.length} files queued`
      );
      const last = results[results.length - 1];
      if (last) seedUpload(last);
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    },
    onSettled: () => setUploadingCount(0)
  });

  const reprocessMutation = useMutation({
    mutationFn: (uploadId) => reprocessDocIntelligenceUpload(uploadId ?? selectedUploadId),
    onSuccess: (_data, uploadId) => {
      if (uploadId) selectUpload(uploadId);
      toast.success('Retry queued');
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Retry failed');
    }
  });

  const discardMutation = useMutation({
    mutationFn: (uploadId) => discardDocIntelligenceUpload(uploadId),
    onSuccess: (_data, uploadId) => {
      toast.success('Document deleted');
      if (uploadId === selectedUploadId) {
        selectUpload(null);
      }
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete document');
    }
  });

  const panel = selectedUpload ? getPanelForUpload(selectedUpload, { mode }) : 'empty';
  const displayPanel =
    isTemplateMode || (templateStudioOpen && panel === 'success') ? panel : panel;
  const basePanel =
    isTemplateMode && panel === 'template' ? 'template' : displayPanel;
  const effectivePanel =
    forceReview && reviewSessionId && selectedUpload ? 'review' : basePanel;
  const meta = getPanelMeta(effectivePanel, selectedUpload, { mode });
  const isUploading = uploadMutation.isPending || uploadingCount > 0;
  const templateIdForSession = reviewSessionId ? templateBySession[reviewSessionId] : null;
  const sidebarUploads = useMemo(() => {
    if (!selectedUpload) return uploads;
    const selectedId = getUploadId(selectedUpload);
    if (!selectedId || uploads.some((row) => getUploadId(row) === selectedId)) {
      return uploads;
    }
    return [selectedUpload, ...uploads];
  }, [uploads, selectedUpload]);

  const rememberTemplate = useCallback(
    (id) => {
      if (!reviewSessionId || !id) return;
      setTemplateBySession((prev) => ({ ...prev, [reviewSessionId]: id }));
    },
    [reviewSessionId]
  );

  const wrapWithShell = (content, { includeTemplate = false } = {}) => {
    if (panel === 'empty' || (!uploads.length && !selectedUpload)) return content;
    return (
      <DocIntelMainCard className="flex min-h-[360px] flex-col">
        <DocIntelPhaseRail
          panel={effectivePanel}
          includeTemplate={includeTemplate && !isTemplateMode}
          mode={mode}
        />
        <div className="min-h-0 flex-1">{content}</div>
      </DocIntelMainCard>
    );
  };

  const renderMainPanel = () => {
    if ((isLoading || isFetching) && !uploads.length && !selectedUpload) {
      return (
        <DocIntelStatePanel
          tone="processing"
          icon={Loader2}
          title="Loading documents…"
          description={
            isTemplateMode
              ? 'Fetching template uploads for this project.'
              : 'Fetching smart uploads for this deal.'
          }
        />
      );
    }

    if (isError && !uploads.length && !selectedUpload) {
      return (
        <DocIntelStatePanel
          tone="error"
          icon={AlertCircle}
          title="Could not load uploads"
          description="Check your connection or permissions, then try again."
          action={
            <button type="button" onClick={() => refetchList()} className={btnPrimary}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          }
        />
      );
    }

    if (!uploads.length && !selectedUpload) {
      return (
        <DocIntelUploadPanel
          mode={mode}
          onUploadFiles={(f) => uploadMutation.mutate(f)}
          isUploading={isUploading}
        />
      );
    }

    switch (panel) {
      case 'processing':
        return wrapWithShell(
          <DocIntelStatePanel
            embedded
            tone="processing"
            icon={Loader2}
            spinIcon
            title={meta.title}
            description={meta.subtitle}
            action={<p className="max-w-xs text-xs leading-relaxed text-slate-500">{meta.hint}</p>}
          />
        );

      case 'failed':
        return wrapWithShell(
          <DocIntelStatePanel
            embedded
            tone="error"
            icon={AlertCircle}
            title={meta.title}
            description={meta.hint}
            action={
              <button
                type="button"
                disabled={reprocessMutation.isPending}
                onClick={() => reprocessMutation.mutate(selectedUploadId)}
                className={btnPrimary}>
                {reprocessMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Retry extraction
              </button>
            }
          />
        );

      case 'review':
        return wrapWithShell(
          <DocIntelReviewPanel
            upload={selectedUpload}
            reviewSessionId={reviewSessionId}
            meta={meta}
            revisitMode={forceReview && selectedUpload?.status !== 'review_pending'}
            onBackToTemplate={
              forceReview
                ? () => {
                    setForceReview(false);
                  }
                : undefined
            }
            onUpdated={() => {
              setApplyResult(null);
              invalidateAll();
              if (forceReview && selectedUpload?.status !== 'review_pending') {
                setForceReview(false);
              }
            }}
          />
        );

      case 'mapping':
        return wrapWithShell(
          <DocIntelMappingPanel
            reviewSessionId={reviewSessionId}
            meta={meta}
            onApplied={(result) => {
              setApplyResult(result);
              invalidateAll();
            }}
          />
        );

      case 'template':
        return wrapWithShell(
          <DocIntelTemplatePanel
            key={templateIdForSession || `session-${reviewSessionId}`}
            dealId={dealId}
            projectId={projectId}
            reviewSessionId={reviewSessionId}
            meta={meta}
            templateId={templateIdForSession}
            studioMode={isTemplateMode}
            onTemplateCreated={rememberTemplate}
            onBack={isTemplateMode ? undefined : () => setTemplateStudioOpen(false)}
            onPromoted={onPromoted}
            onOpenReview={() => setForceReview(true)}
          />
        );

      case 'success':
        if (templateStudioOpen) {
          return wrapWithShell(
            <DocIntelTemplatePanel
              key={templateIdForSession || `session-${reviewSessionId}`}
              dealId={dealId}
              reviewSessionId={reviewSessionId}
              meta={meta}
              templateId={templateIdForSession}
              onTemplateCreated={rememberTemplate}
              onBack={() => setTemplateStudioOpen(false)}
            />,
            { includeTemplate: true }
          );
        }
        return wrapWithShell(
          <DocIntelApplyPanel
            dealId={dealId}
            meta={meta}
            applyResult={applyResult ?? selectedUpload?.apply_summary ?? null}
            onOpenTemplateStudio={() => setTemplateStudioOpen(true)}
          />
        );

      default:
        return (
          <DocIntelUploadPanel
            mode={mode}
            onUploadFiles={(f) => uploadMutation.mutate(f)}
            isUploading={isUploading}
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
      {(uploads.length > 0 || selectedUpload) && (
        <DocIntelSidebar
          mode={mode}
          uploads={sidebarUploads}
          selectedUploadId={selectedUploadId}
          onSelect={(id) => {
            setApplyResult(null);
            setForceReview(false);
            if (!isTemplateMode) setTemplateStudioOpen(false);
            selectUpload(id);
          }}
          onUploadFiles={(f) => uploadMutation.mutate(f)}
          onRetryUpload={(id) => reprocessMutation.mutate(id)}
          retryingUploadId={reprocessMutation.isPending ? reprocessMutation.variables : null}
          onDiscardUpload={(id) => discardMutation.mutate(id)}
          discardingUploadId={discardMutation.isPending ? discardMutation.variables : null}
          isUploading={isUploading}
        />
      )}

      <div className="min-w-0 flex-1">{renderMainPanel()}</div>
    </div>
  );
};

export default DocIntelWorkspace;
